import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { VERIFY_EMAIL, RESEND_OTP } from '../../../graphql/mutation'
import { GET_USER_BY_ID } from '../../../graphql/query'

type Props = {
  user: any
  currentPlan: any
}

const VerifyEmail: React.FC<Props> = ({ user, currentPlan }) => {
  const [otp, setOtp] = useState('')
  const [verifyEmail] = useMutation(VERIFY_EMAIL, {
    refetchQueries: [
      {
        query: GET_USER_BY_ID
      }
    ]
  })
  const [reSendOtp] = useMutation(RESEND_OTP)
  const navigate = useNavigate()
  const [remainingTime, setRemainingTime] = useState(60)
  const [countdownActive, setCountdownActive] = useState(false) // Start the countdown

  useEffect(() => {
    if (countdownActive) {
      const interval = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime < 1) {
            setCountdownActive(false) // Stop the countdown
            return 0
          }
          return prevTime - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [countdownActive])

  useEffect(() => {
    if (!!window.sessionStorage.getItem('botgo-email-otp-session-timestamp')) {
      const currentTime = Date.now()
      const timeCounterStarted = new Date(
        parseInt(window.sessionStorage.getItem('botgo-email-otp-session-timestamp') as string)
      ).valueOf()
      const timeEllapsed = currentTime - timeCounterStarted
      const secondsEllapsed = Math.round(timeEllapsed / 1000)
      const secondsLeft = 60 - secondsEllapsed
      if (secondsLeft > 0) {
        setRemainingTime(secondsLeft)
        setCountdownActive(true)
      }
    } else {
      window.sessionStorage.setItem('botgo-email-otp-session-timestamp', Date.now().toString())
      setCountdownActive(true)
    }
  }, [])
  const Navigate = () => {
    localStorage.clear()
    document.location.reload()
  }
  const VerifyEmailSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    verifyEmail({
      variables: {
        input: {
          email: user.email,
          otp: otp
        }
      }
    })
      .then((res: any) => {
        if (res.data.verifyEmail.status >= 200 && res.data.verifyEmail.status < 300) {
          toast.success(res.data.verifyEmail.message)
          if (currentPlan?.clientPackageId === null) {
            navigate('/change-plan')
          } else {
            navigate('/bots/all-bots')
          }
          // remove timer data from sessionStorage
          if (!!window.sessionStorage.getItem('botgo-email-otp-session-timestamp')) {
            window.sessionStorage.removeItem('botgo-email-otp-session-timestamp')
          }
        } else {
          toast.error(res.data.verifyEmail.message)
        }
      })
      .catch((err) => {
        toast.error('Sorry! Unexpected Error.')
        console.log(err)
      })
  }

  const ResendOtpHandle = () => {
    if (!!window.sessionStorage.getItem('botgo-email-otp-session-timestamp')) {
      window.sessionStorage.removeItem('botgo-email-otp-session-timestamp')
    }
    reSendOtp({
      variables: {
        input: {
          email: user.email
        }
      }
    })
      .then((res: any) => {
        if (res.data.resendOtp.status >= 200 && res.data.resendOtp.status < 300) {
          toast.success('OTP resent successfully')
          setRemainingTime(60) // Reset the remaining time to 5 minutes
          setCountdownActive(true) // Start the countdown
          if (!window.sessionStorage.getItem('botgo-email-otp-session-timestamp')) {
            window.sessionStorage.setItem(
              'botgo-email-otp-session-timestamp',
              Date.now().toString()
            )
          }
        } else {
          toast.error('Sorry! Unexpected Error')
        }
      })
      .catch(() => {
        toast.error('Sorry! Unexpected Error.')
      })
  }

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = timeInSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <>
      <div
        className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
        style={{
          // Replace the path with the actual URL of your background image
          backgroundImage: `url('/media/illustrations/sketchy-1/14.png')`
        }}
      >
        <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
          <button className='btn primary page-link'>
            <img alt='Logo' src='/media/logos/logo.png' className='h-65px' />
          </button>
          <div className='w-lg-505px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <form className='form w-100' noValidate onSubmit={VerifyEmailSubmit}>
              {/* Form content goes here */}
              <div className='text-center mb-10'>
                <h1 className='text-dark mb-3'>Verify Email Address</h1>
                <div className='text-gray-400 fw-bold fs-4 d-flex'>
                  Enter the OTP sent to &nbsp;{'   '}
                  <div></div>
                  <div className='link-primary fw-bolder'>{user.email}</div>
                </div>
              </div>
              <div className='fv-row mb-10'>
                <div className='d-flex justify-content-between mt-n5'>
                  <div className='d-flex flex-stack mb-2'>
                    <label className='form-label fw-bolder text-dark fs-6 mb-0'>OTP</label>
                  </div>
                </div>
                <input
                  type='text'
                  autoComplete='off'
                  placeholder='Enter your OTP'
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className={clsx('form-control form-control-lg form-control-solid')}
                />
              </div>
              <div className='fv-row mb-10'>
                {countdownActive && (
                  <p className='text-gray-400 fw-bold fs-5 d-flex'>
                    You can resend OTP after: &nbsp;
                    <span className='link-primary fw-bolder'>{formatTime(remainingTime)}</span>
                  </p>
                )}
                <br />
                {!countdownActive && (
                  <div className='d-flex justify-content-between mt-n5'>
                    <div className='d-flex flex-stack mb-2'>
                      <label className='form-label fw-bolder text-dark fs-6 mb-0'>
                        Didn't you receive the OTP?
                      </label>
                      <span
                        className='link-primary fs-6 fw-bolder'
                        style={{ marginLeft: '5px', cursor: 'pointer' }}
                        onClick={ResendOtpHandle}
                      >
                        Resend OTP
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className='text-center'>
                <button type='submit' className='btn btn-lg btn-primary w-100 mb-5'>
                  <span className='indicator-label'>Verify</span>
                </button>
              </div>

              <button
                type='button'
                className='btn btn-lg btn-light-primary w-100 mb-5'
                onClick={Navigate}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
        <div className='d-flex flex-center flex-column-auto p-10'>
          <div className='d-flex align-items-center fw-bold fs-6'>
            <button className='btn primary page-link text-muted text-hover-primary px-2'>
              About
            </button>
            <button className='btn primary page-link text-muted text-hover-primary px-2'>
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default VerifyEmail
